import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { unitAPI, getImageUrl } from '../../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
// import { Card, Form, Button, Table, Spinner, Modal } from 'react-bootstrap';
// import DatePicker from 'react-datepicker';
// import 'react-datepicker/dist/react-datepicker.css';
import * as visitorAPI from '../../services/visitor.api';
import * as utils from '../../utils/dateUtils';
import { useSettings } from '../../contexts/SettingsContext';
import PageTitle from '../shared/PageTitle';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const VisitorSearch = () => {
  const { settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  // State for search filters
  const [filters, setFilters] = useState({
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    unit_id: ''
  });

  // State for search results and UI
  const [visitors, setVisitors] = useState([]);
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(false);
  // const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage] = useState(10);
  const [error, setError] = useState(null);

  // Create local placeholder image using data URL
  const createLocalPlaceholder = (text = 'No Image', width = 200, height = 200) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);

    // Border
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);

    // Text
    ctx.fillStyle = '#6c757d';
    ctx.font = '16px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, width / 2, height / 2);

    return canvas.toDataURL();
  };

  // Initialize visitor search results with image URL preprocessing
  const preprocessVisitorImageUrls = (visitors) => {
    if (!visitors || !Array.isArray(visitors)) return [];

    return visitors.map(visitor => {
      const processedVisitor = { ...visitor };

      // Process photo URL
      if (processedVisitor.photo_url) {
        processedVisitor.photo_url = getImageUrl(processedVisitor.photo_url);
      }

      // Process ID document URL
      if (processedVisitor.id_document_url) {
        processedVisitor.id_document_url = getImageUrl(processedVisitor.id_document_url);
      }

      return processedVisitor;
    });
  };

  // Define fetchInitialData outside useEffect so it can be called from retry button
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors

      // Set default date range to last 30 days
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);

      setFilters(prev => ({
        ...prev,
        dateFrom: thirtyDaysAgo.toISOString().split('T')[0],
        dateTo: today.toISOString().split('T')[0]
      }));

      // Load villas for filter dropdown
      try {
        const villasRes = await unitAPI.getAllUnits();
        if (villasRes && villasRes.data) {
          setVillas(villasRes.data || []);
        } else {
          console.error('Invalid units response:', villasRes);
          setError('Failed to load unit data');
        }
      } catch (unitError) {
        console.error('Error loading units:', unitError);
        setError('Failed to load unit data for filtering');
        // Continue with the search even if units fail to load
      }

      // Load visitors with default filters
      await searchVisitors();
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load initial data. Please try again.');
      toast.error('Failed to load initial data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Reset all filters
  const handleResetFilters = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    // Reset filters state
    setFilters({
      searchTerm: '',
      dateFrom: thirtyDaysAgo.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0],
      status: '',
      unit_id: ''
    });

    // Reset current page to 1
    setCurrentPage(1);

    // Create search params with reset values
    const searchParams = {
      page: 1,
      limit: resultsPerPage,
      organization_id: JSON.parse(sessionStorage.getItem('user') || '{}').organization_id,
      status: 'completed',
      has_time_out: true,
      date_from: thirtyDaysAgo.toISOString(),
      date_to: today.toISOString()
    };

    // Execute search immediately with reset parameters
    searchVisitors(searchParams);
  };

  // Handle search form submission
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
    await searchVisitors();
  };

  // Function to search visitors with current filters
  const searchVisitors = async (overrideParams = null) => {
    try {
      setLoading(true);
      setError(null);

      // Get user's organization ID from session storage
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const organizationId = user.organization_id;

      if (!organizationId) {
        setError('Organization context not found. Please try logging out and back in.');
        setLoading(false);
        return;
      }

      // Format dates properly
      const formattedDateFrom = filters.dateFrom ? new Date(filters.dateFrom).toISOString() : null;
      const formattedDateTo = filters.dateTo ? new Date(filters.dateTo).toISOString() : null;

      // Prepare search parameters
      const searchParams = overrideParams || {
        page: currentPage,
        limit: resultsPerPage,
        organization_id: organizationId,
        status: 'completed',  // Only show completed visitors
        has_time_out: true,  // Ensure they have checked out
        search: filters.searchTerm,  // Changed back to search for API compatibility
        date_from: formattedDateFrom,
        date_to: formattedDateTo,
        unit_id: filters.unit_id
      };

      // Remove null, undefined, and empty string values
      Object.keys(searchParams).forEach(key => {
        if (searchParams[key] === undefined || searchParams[key] === null || searchParams[key] === '') {
          delete searchParams[key];
        }
      });

      console.log('Search params:', searchParams);  // Debug log

      // Make API request
      const response = await visitorAPI.searchVisitors(searchParams);

      if (response && response.data) {
        const visitors = Array.isArray(response.data) ? response.data :
          response.data.visitors ? response.data.visitors : [];

        // Process visitor data
        const processedVisitors = preprocessVisitorImageUrls(visitors);
        setVisitors(processedVisitors);

        // Update pagination
        const total = response.data.total || visitors.length;
        setTotalResults(total);
      }
    } catch (error) {
      console.error('Error searching visitors:', error);
      setError('Failed to search visitors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);
    setTimeout(() => {
      scrollToSectionTop('visitor-search-section');
    }, 50);

    // Update results when page changes
    try {
      setLoading(true);

      // Prepare search parameters with new page
      const searchParams = {
        page: newPage,
        limit: resultsPerPage,
        ...filters
      };

      // Call API with search parameters
      const response = await visitorAPI.searchVisitors(searchParams);

      if (response.data) {
        // Preprocess all image URLs
        const processedVisitors = preprocessVisitorImageUrls(response.data.visitors || []);
        setVisitors(processedVisitors);
      }
    } catch (error) {
      console.error('Error changing page:', error);
      toast.error('Failed to load page. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // View visitor details
  const handleViewDetails = async (visitor) => {
    try {
      // First set initial visitor data to show loading state
      // Apply preprocessing to ensure images work while loading
      const initialProcessedVisitor = preprocessVisitorImageUrls([visitor])[0];
      setSelectedVisitor(initialProcessedVisitor);
      setShowDetailsModal(true);
      setLoading(true);

      // Get user's organization ID from session storage
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const organizationId = user.organization_id;

      if (!organizationId) {
        setError('Organization context not found. Please try logging out and back in.');
        setLoading(false);
        return;
      }

      // Use the search endpoint with organization context and specific visitor ID
      console.log(`Fetching details for visitor ID: ${visitor.id}`);
      const response = await visitorAPI.searchVisitors({
        organization_id: organizationId,
        visitor_ids: [visitor.id],
        include_photos: true,
        limit: 1
      });

      console.log('Search response for visitor details:', response);

      if (response && response.data && response.data.visitors && response.data.visitors.length > 0) {
        const visitorData = response.data.visitors[0];
        console.log('Found visitor data:', visitorData);

        // Process the returned visitor data to fix image URLs
        const processedVisitor = { ...visitorData };

        // Handle photo URL using shared utility
        if (processedVisitor.photo_url) {
          processedVisitor.photo_url = getImageUrl(processedVisitor.photo_url);
        }

        // Handle ID document URL using shared utility
        if (processedVisitor.id_document_url) {
          processedVisitor.id_document_url = getImageUrl(processedVisitor.id_document_url);
        }

        // Update the visitor with the processed data
        setSelectedVisitor(processedVisitor);
      }
    } catch (error) {
      console.error('Error fetching visitor details:', error);
      toast.error('Error loading visitor details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total pages for pagination
  const totalPages = Math.ceil(totalResults / resultsPerPage);

  // Format date and time (use the utility function from dateUtils.js)
  function formatDateTime(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  // Add calculateDuration function
  const calculateDuration = (timeIn, timeOut) => {
    if (!timeIn || !timeOut) return 'Not available';

    // Use the shared utility function
    return utils.calculateDuration(timeIn, timeOut);
  };

  // Close details modal
  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedVisitor(null);
  };

  // Determine visitor status based on check-in/check-out times
  const getVisitorStatus = (visitor) => {
    const now = new Date();
    const checkInTime = visitor.time_in ? new Date(visitor.time_in) : null;
    const checkOutTime = visitor.time_out ? new Date(visitor.time_out) : null;

    if (checkOutTime) {
      return 'Completed';
    } else if (checkInTime && checkInTime <= now) {
      return 'Active';
    } else if (checkInTime && checkInTime > now) {
      return 'Upcoming';
    } else {
      return visitor.status || 'Pending';
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-success';
      case 'completed': return 'bg-secondary';
      case 'denied': return 'bg-danger';
      case 'upcoming': return 'bg-primary';
      case 'pending': return 'bg-warning';
      default: return 'bg-info';
    }
  };

  // Add a test function to create a visitor and then search for it
  const runSearchTest = async () => {
    try {
      setLoading(true);
      setError(null);
      toast.info('Running search test...');

      // Test search endpoint
      try {
        console.log('Testing search endpoint...');
        const testResponse = await axios.get(
          // Force localhost in development, ignore environment variable if it points to production
          `${(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
            ? 'https://nivaas.app'
            : (process.env.REACT_APP_API_URL || 'https://nivaas.app')}/api/visitors/test-search`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (testResponse.data && testResponse.data.success) {
          toast.success(`Test completed: ${testResponse.data.searchResults.total} visitors found. New visitor ${testResponse.data.searchResults.foundNewVisitor ? 'was' : 'was NOT'} found.`);

          // If the test was successful, refresh the search results
          await searchVisitors();
        } else {
          console.error('Test failed with response:', testResponse);
          // toast.error('Test failed. See console for details.');
        }
      } catch (error) {
        console.error('Error running search test:', error);
        // toast.error(`Test error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error running search test:', error);
      // toast.error(`Test error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  function formatTimeDuration(duration) {
    if (!duration || isNaN(duration)) return 'Not specified';

    const totalMinutes = Math.round(duration * 60);
    const totalDays = Math.floor(totalMinutes / (24 * 60));

    let remainingMinutes = totalMinutes;

    let months = Math.floor(totalDays / 30);
    remainingMinutes -= months * 30 * 24 * 60;

    let weeks = Math.floor((remainingMinutes / (7 * 24 * 60)));
    remainingMinutes -= weeks * 7 * 24 * 60;

    let days = Math.floor(remainingMinutes / (24 * 60));
    remainingMinutes -= days * 24 * 60;

    let hours = Math.floor(remainingMinutes / 60);
    let minutes = remainingMinutes % 60;

    let parts = [];
    if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
    if (weeks > 0) parts.push(`${weeks} week${weeks > 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);

    return parts.length > 0 ? parts.join(' ') : '0 minutes';
  }

  return (
    <div className="container-fluid py-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <PageTitle
        icon="fa-search"
        title="Visitor Records Search"
        action={
          <div>
            <Link to="/guard/dashboard" className="btn btn-outline-primary mb-2 me-2">
              <i className="fas fa-arrow-left me-2"></i>
              Back to Dashboard
            </Link>
            <Link to="/guard/visitors" className="btn btn-outline-secondary mb-2 me-2">
              <i className="fas fa-users me-2"></i>
              Visitor Management
            </Link>
            <button
              className="btn btn-outline-secondary mb-2 me-2"
              onClick={handleResetFilters}
            >
              <i className="fas fa-redo me-2"></i>
              Reset Filters
            </button>
            {/* <button 
              className="btn btn-outline-info mb-2" 
              onClick={runSearchTest}
              disabled={loading}
            >
              <i className="fas fa-vial me-2"></i>
              Test Search
            </button> */}
          </div>
        }
      />

      {/* Error message */}
      {error && (
        <div className="alert alert-danger mb-4">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <div className="float-end">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger me-2"
              onClick={() => {
                setLoading(true);
                setTimeout(() => {
                  fetchInitialData();
                }, 500);
              }}
            >
              <i className="fas fa-sync-alt me-1"></i> Retry
            </button>
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        </div>
      )}

      {/* Search Form */}
      <div className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleSearch}>
            <div className="row g-3">
              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or purpose..."
                  name="searchTerm"
                  value={filters.searchTerm || ''}
                  onChange={handleFilterChange}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
              </div>

              <div className="col-md-3">
                <input
                  type="date"
                  className="form-control"
                  placeholder="From Date"
                  name="dateFrom"
                  value={filters.dateFrom || ''}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-3">
                <input
                  type="date"
                  className="form-control"
                  placeholder="To Date"
                  name="dateTo"
                  value={filters.dateTo || ''}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-md-4">
                <select
                  className="form-select"
                  name="unit_id"
                  value={filters.unit_id || ''}
                  onChange={handleFilterChange}
                >
                  <option value="">All Units</option>
                  {villas.map(villa => (
                    <option key={villa.id} value={villa.id}>
                      {villa.villa_number}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-search me-1"></i> Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary py-2"
                    onClick={handleResetFilters}
                  >
                    <i className="fas fa-undo me-1"></i> Reset
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Search Results */}
      <div className="card" id="visitor-search-section">
        <div
          className="card-header"
          style={{
            backgroundColor: settings.block_title_bg_color || '#bd2f00',
            color: settings.block_title_color || '#ffffff'
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Search Results</h5>
            <span className="badge bg-primary">{totalResults} Record(s) Found</span>
          </div>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Searching visitor records...</p>
            </div>
          ) : visitors.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-search fa-3x text-muted mb-3"></i>
              <p className="text-muted">No visitors found matching your search criteria</p>
              <p className="small text-muted">Try adjusting your search filters or try a different search term</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Visitor Name</th>
                    <th>Unit</th>
                    <th>Purpose</th>
                    <th>Status</th>
                    <th>Check-In Time</th>
                    <th>Check-Out Time</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <i className="fas fa-history fa-2x mb-2 text-muted d-block"></i>
                        No visitor history found
                      </td>
                    </tr>
                  ) : (
                    visitors.map(visitor => {
                      const status = getVisitorStatus(visitor);
                      return (
                        <tr key={visitor.id}>
                          <td>{visitor.id}</td>
                          <td>{visitor.visitor_name}</td>
                          <td>{visitor.villa_number}</td>
                          <td>{visitor.purpose}</td>
                          <td>
                            <span className={`badge ${getStatusBadgeColor(status)}`}>
                              {status}
                            </span>
                          </td>
                          <td>{formatDateTime(visitor.time_in)}</td>
                          <td>{formatDateTime(visitor.time_out)}</td>
                          <td>{calculateDuration(visitor.time_in, visitor.time_out)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => handleViewDetails(visitor)}
                            >
                              <i className="fas fa-eye"></i> Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer">
            <nav>
              <ul className="pagination justify-content-center mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Visitor Details Modal */}
      {showDetailsModal && selectedVisitor && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
          <div className="modal d-block" tabIndex="-1" style={{ zIndex: 1060 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered" style={{ maxWidth: '850px' }}>
              <div className="modal-content border-0 shadow">
                <div
                  className="modal-header"
                  style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}
                >
                  <h5 className="modal-title">
                    <i className="fas fa-user-circle me-2"></i>
                    Visitor Details: {selectedVisitor.visitor_name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseDetails}
                    aria-label="Close"
                    style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                  ></button>
                </div>
                <div className="modal-body p-3">
                  <div className="row g-0">
                    {/* Left Side - Visitor Information */}
                    <div className="col-md-7 p-3 border-end">
                      <div className="visitor-info mb-3">
                        <h5 className="border-bottom pb-2 mb-2">
                          {selectedVisitor.visitor_name}
                          <span className={`ms-2 badge ${getStatusBadgeColor(getVisitorStatus(selectedVisitor))}`}>
                            {getVisitorStatus(selectedVisitor)}
                          </span>
                        </h5>

                        <div className="d-flex justify-content-between border-bottom py-2">
                          <strong>Phone:</strong>
                          <span>{selectedVisitor.phone || 'Not provided'}</span>
                        </div>

                        <div className="d-flex justify-content-between border-bottom py-2">
                          <strong>Unit:</strong>
                          <span>Villa {selectedVisitor.villa_number}</span>
                        </div>

                        <div className="d-flex justify-content-between border-bottom py-2">
                          <strong>Purpose:</strong>
                          <span>{selectedVisitor.purpose || 'Not specified'}</span>
                        </div>
                      </div>

                      <div className="visit-details">
                        <h6 className="mb-2 text-muted">Visit Timeline</h6>

                        <div className="d-flex justify-content-between border-bottom py-2">
                          <strong>Check-In Time:</strong>
                          <span className="text-success">{selectedVisitor.time_in ? formatDateTime(selectedVisitor.time_in) : 'Not checked in'}</span>
                        </div>

                        <div className="d-flex justify-content-between border-bottom py-2">
                          <strong>Check-Out Time:</strong>
                          <span className={selectedVisitor.time_out ? 'text-success' : 'text-danger'}>
                            {selectedVisitor.time_out ? formatDateTime(selectedVisitor.time_out) : 'Not checked out'}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between border-bottom py-2">
                          <strong>Duration:</strong>
                          <span>
                            {selectedVisitor.time_in && selectedVisitor.time_out ?
                              (() => {
                                const start = new Date(selectedVisitor.time_in);
                                const end = new Date(selectedVisitor.time_out);
                                const diff = Math.abs(end - start);
                                const hours = Math.floor(diff / (1000 * 60 * 60));
                                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                                return `${hours}h ${minutes}m`;
                              })() :
                              'In Progress'}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between py-2">
                          <strong>Expected Duration:</strong>
                          <span>{selectedVisitor.expected_duration ? formatTimeDuration(selectedVisitor.expected_duration) : 'Not specified'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right Side - Photos */}
                    <div className="col-md-5 bg-light p-3">
                      <h6 className="text-center mb-2 text-muted">Visitor Verification</h6>

                      {/* Visitor Photo */}
                      <div className="mb-3">
                        <p className="small text-center text-muted mb-1">Visitor Photo</p>
                        <div className="d-flex justify-content-center">
                          <div className="img-container d-flex justify-content-center align-items-center border bg-white rounded" style={{ height: '160px', width: '100%' }}>
                            {selectedVisitor.photo_url ? (
                              <img
                                src={selectedVisitor.photo_url}
                                alt="Visitor"
                                className="img-fluid"
                                style={{ maxHeight: '145px', maxWidth: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  console.error(`Error loading photo from URL: ${selectedVisitor.photo_url}`);
                                  // Try alternative URL format
                                  if (!selectedVisitor.photo_url.includes('/api/visitors/photo/') &&
                                    selectedVisitor.photo_url.includes('/uploads/photos/')) {
                                    const filename = selectedVisitor.photo_url.split('/').pop();
                                    const newUrl = `${(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
                                      ? 'https://nivaas.app'
                                      : (process.env.REACT_APP_API_URL || 'https://nivaas.app')}/api/visitors/photo/${filename}`;
                                    console.log(`Trying alternative URL: ${newUrl}`);
                                    // Store the previous URL so we don't retry the same URL
                                    const prevUrl = e.target.src;
                                    e.target.src = newUrl;

                                    // Add a one-time event listener to handle the case where the fallback also fails
                                    e.target.addEventListener('error', () => {
                                      console.error(`Fallback URL also failed: ${newUrl}`);
                                      if (e.target.src !== prevUrl && e.target.src !== createLocalPlaceholder('No Photo')) {
                                        e.target.src = createLocalPlaceholder('No Photo');
                                      }
                                    }, { once: true });
                                  } else {
                                    e.target.src = createLocalPlaceholder('No Photo');
                                  }
                                }}
                              />
                            ) : (
                              <img
                                src={createLocalPlaceholder('No Photo')}
                                alt="No visitor photo"
                                className="img-fluid"
                                style={{ maxHeight: '145px', maxWidth: '100%', objectFit: 'contain' }}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* ID Document Photo */}
                      <div>
                        <p className="small text-center text-muted mb-1">ID Document</p>
                        <div className="d-flex justify-content-center">
                          <div className="img-container d-flex justify-content-center align-items-center border bg-white rounded" style={{ height: '160px', width: '100%' }}>
                            {selectedVisitor.id_document_url ? (
                              <img
                                src={selectedVisitor.id_document_url}
                                alt="ID Document"
                                className="img-fluid"
                                style={{ maxHeight: '145px', maxWidth: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  console.error(`Error loading ID document from URL: ${selectedVisitor.id_document_url}`);
                                  // Try alternative URL format
                                  if (!selectedVisitor.id_document_url.includes('/api/visitors/id-document/') &&
                                    selectedVisitor.id_document_url.includes('/uploads/id_docs/')) {
                                    const filename = selectedVisitor.id_document_url.split('/').pop();
                                    const newUrl = `${(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
                                      ? 'https://nivaas.app'
                                      : (process.env.REACT_APP_API_URL || 'https://nivaas.app')}/api/visitors/id-document/${filename}`;
                                    console.log(`Trying alternative URL: ${newUrl}`);
                                    // Store the previous URL so we don't retry the same URL
                                    const prevUrl = e.target.src;
                                    e.target.src = newUrl;

                                    // Add a one-time event listener to handle the case where the fallback also fails
                                    e.target.addEventListener('error', () => {
                                      console.error(`Fallback URL also failed: ${newUrl}`);
                                      if (e.target.src !== prevUrl && e.target.src !== createLocalPlaceholder('No ID Document')) {
                                        e.target.src = createLocalPlaceholder('No ID Document');
                                      }
                                    }, { once: true });
                                  } else {
                                    e.target.src = createLocalPlaceholder('No ID Document');
                                  }
                                }}
                              />
                            ) : (
                              <img
                                src={createLocalPlaceholder('No ID Document')}
                                alt="No ID document"
                                className="img-fluid"
                                style={{ maxHeight: '145px', maxWidth: '100%', objectFit: 'contain' }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <div className="w-100 d-flex justify-content-between">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleCloseDetails}>
                      <i className="fas fa-times me-2"></i>
                      Close
                    </button>

                    {selectedVisitor.status === 'active' && !selectedVisitor.time_out && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => {
                          // Could add checkout handling here if needed
                          handleCloseDetails();
                        }}
                      >
                        <i className="fas fa-sign-out-alt me-2"></i>
                        Check Out
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VisitorSearch; 